/* eslint-disable max-len */
import {makeObservable, observable, action, flow, computed} from 'mobx';
import {App} from 'antd';
import {isEmpty} from 'lodash';
import isEqual from 'lodash/isEqual';
import {evaluateUpload, renameFileApi, deleteFileApi} from '@/api/upload';
import {transformToEvaluatorList} from '@/utils/index'; // 引入 StepStore
import {handleRequestError} from '@/utils/errorHandler';
import {readFileAsText} from '@/utils/readFileAsText';
import StepStore from './StepStore';

export class EvaluatorStore {
    job_id: string = '';
    uploadLoading: boolean = false;
    // 评估器列表 把后台返回的数据处理成这样 transformToEvaluatorList
    // star_evaluator 如果没有返回值 就赋值为evaluators[0]
    evaluators: FileInfo[] = [];
    selectedFileType: 'evaluator' | 'dataset' | 'simulator' | null = null;
    // 星标评估器名称
    star_evaluator: object|FileInfo = {};
    // 评估集列表
    dataset: FileInfo[] = [];
    // 仿真环境
    simulators: FileInfo[] = [];
    // 当前编辑的评估器 id
    selectedFileId: string | null = null;
    // 当前选中的评估器
    editingFile: FileInfo | undefined = undefined;
    // 当前选中的评估器信息
    selectedFileInfo: FileInfo | undefined = undefined;
    // 是否为空状态
    isEmpty: boolean = true;
    // 执行 loading
    runLoading: boolean = false;

    initialSnapshot: any = null;

    constructor(job_id: string) {
        this.job_id = job_id;
        makeObservable(this, {
            evaluators: observable,
            star_evaluator: observable,
            dataset: observable,
            simulators: observable,
            selectedFileId: observable,
            editingFile: observable,
            selectedFileType: observable,
            isEmpty: observable,
            isMaxCount: computed,
            runLoading: observable,
            initialSnapshot: observable,
            setEvaluators: action.bound,
            setJobId: action.bound,
            addFile: action.bound,
            updateFile: action.bound,
            renameFile: flow.bound,
            toggleFileStar: action.bound,
            deleteFile: flow.bound,
            getFile: action.bound,
            uploadFile: flow.bound,
            fetchFileInfo: flow.bound,
            reset: action.bound,
            setSelectedFile: action.bound,
            deleteFilesLocally: action.bound,
            renameFileLocally: action.bound,
            uploadFileLocally: action.bound,
            selectedFile: computed,
            hasFiles: computed,
            hasChanged: computed,
            isEmptyEvaluatorsStarEvaluator: computed,
            resetSnapshot: action.bound
        });
    }

    // evaluators 评估算法为空 或者星标文件不存在
    get isEmptyEvaluatorsStarEvaluator() {
        if ((this.evaluators.length === 0) || (isEmpty(this.star_evaluator))) {
            return true;
        }
    }

    get hasChanged() {
        // 没有做内容改变
        if (!this.initialSnapshot) {
            // eslint-disable-next-line no-console
            console.log('[hasChanged] No initialSnapshot, returning false');
            return false;
        }
        const currentState = this.toJSON();
        const changed = !isEqual(this.initialSnapshot, currentState);

        return changed;
    }

    // 获取选中的评估器
    get selectedFile(): FileInfo | null {
        if (!this.selectedFileId || !this.selectedFileType) {
            return null;
        }

        let list: FileInfo[] = [];
        switch (this.selectedFileType) {
            case 'evaluator':
                list = this.evaluators;
                break;
            case 'dataset':
                list = this.dataset;
                break;
            case 'simulator':
                list = this.simulators;
                break;
        }
        return list.find(f => f.id === this.selectedFileId) || null;
    }

    // 是否有评估器
    get hasFiles(): boolean {
        return this.evaluators.length > 0;
    }

    // 是否是最大上传个数
    get isMaxCount(): boolean {
        return this.evaluators.length >= 10;
    }

    setEvaluators(evaluators: FileInfo[]) {
        this.evaluators = evaluators;
        this.updateEmptyState();
    }
    setStarEvaluator(star_evaluator, callback) {
        if (star_evaluator) {
            const file = this.evaluators.find(item => item.name === star_evaluator.file_name);
            this.star_evaluator = file;
        } else if (this.evaluators.length) {
            this.star_evaluator = this.evaluators[0];
            callback({
                star_evaluator: this.star_evaluator?.file_name
            });
        }
    }

    setJobId(job_id: string) {
        this.job_id = job_id;
    }
    setEditingFile(file: FileInfo) {
        this.editingFile = file;
    }

    // 添加评估器
    addFile(this: EvaluatorStore, evaluator: FileInfo) {
        this.evaluators.push(evaluator);
        this.setSelectedFile(evaluator.id, 'evaluator');
        this.updateEmptyState();
    }

    // 重命名评估器本地操作
    renameFileLocally(id: string, newName: string) {
        const index = this.evaluators.findIndex(e => e.id === id);
        if (index !== -1) {
            this.evaluators[index].name = newName;
        }
    }

    // 重命名评估器
    * renameFile(id: string, newName: string): Generator<any, void, unknown> {
        const index = this.evaluators.findIndex(e => e.id === id);
        if (index !== -1) {
            const oldName = this.evaluators[index].name;
            const params = {
                job_id: this.job_id,
                old_name: oldName,
                new_name: newName,
                tag: 'evaluator' // 评估算法'
            };
            const resp: ApiResponse = yield renameFileApi(params);
            if (resp.code === '0') {
                this.evaluators[index].name = newName;
            }
        }
    }

    // 更新评估器
    updateFile(id: string, newEvaluator: Partial<FileInfo>) {
        const index = this.evaluators.findIndex(e => e.id === id);
        if (index !== -1) {
            this.evaluators[index] = {
                ...this.evaluators[index],
                ...newEvaluator
            };
        }
    }

    /** 删除文件本地操作 */
    deleteFilesLocally(id: string, stepStore: StepStore) {
        this.evaluators = this.evaluators.filter(e => e.id !== id);
        if (this.selectedFileId === id) {
            this.setSelectedFile(null, null);
        }
        if (id === this.star_evaluator.id) {
            if (this.evaluators.length) {
                this.star_evaluator = this.evaluators[0];
            } else {
                this.star_evaluator = {};
                stepStore.markCompleted(1, false);
            }
        }
        this.updateEmptyState();
    }

    // 删除评估器
    * deleteFile(id: string, name: string, stepStore: StepStore, message?: ReturnType<typeof App.useApp>['message']): Generator<any, void, unknown> {
        const params = {
            job_id: this.job_id,
            file_name: name,
            tag: 'evaluator'
        };
        const resp: ApiResponse = yield deleteFileApi(params);
        if (resp.code === '0') {
            message?.success('删除成功');
            yield this.deleteFilesLocally(id, stepStore);
        }
    }

    // 切换星标
    toggleFileStar(id: string, callback = null) {
        // 获取文件评估器
        const evaluator = this.getFile(id);
        // 如果存在评估器
        if (evaluator) {
            // 将评估器赋值给 star_evaluator 属性
            this.star_evaluator = evaluator;
        }
        callback?.({
            star_evaluator: this.star_evaluator.file_name || this.star_evaluator.name
        });
    }

    // 获取评估器
    getFile(id: string): FileInfo | undefined {
        return this.evaluators.find(e => e.id === id);
    }

    // 重置
    reset() {
        this.evaluators = [];
        this.setSelectedFile(null, null);
        this.updateEmptyState();
        this.stepStore.reset(); // 重置步骤状态et
    }

    // 设置选中的评估器
    setSelectedFile(id: string | null, type: 'evaluator' | 'dataset' | 'simulator' | null) {
        this.selectedFileId = id;
        this.selectedFileType = type;
    }

    resetSnapshot() {
        this.initialSnapshot = JSON.parse(JSON.stringify(this.toJSON()));
    }

    /** 本地处理上传文件 */
    uploadFileLocally(evaluatorFiles: EvaluatorUploadParams['evaluator']) {
        // 兼容 antd Upload 的结构
        const realFiles = evaluatorFiles.map(item => item.originFileObj || item);

        const newList = transformToEvaluatorList(realFiles).map(fileInfo => {
            const originFile = realFiles.find(f => f.name === fileInfo.name);
            return {
                ...fileInfo,
                originFile,
                code: '',
                isCodeEdited: true // 用户上传的文件标记为已编辑
            } as any;
        });
        Promise.all(
            newList.map(async item => {
                if (item.originFile) {
                    try {
                        const {content, encoding} = await readFileAsText(item.originFile);
                        item.code = content;
                        item.encoding = encoding;
                    } catch (e) {
                        console.error(`读取文件 ${item.name} 失败:文件类型unknown`, e);
                        item.code = '';
                    }
                }
                return item;
            })
        ).then(finalList => {
        // 拼接旧文件 + 新文件
            const mergedList = [...this.evaluators, ...finalList];

            // 用文件名作为唯一 key，同名的用新文件覆盖旧文件
            const fileMap = new Map<string, any>();
            mergedList.forEach(file => {
                fileMap.set(file.name, file);
            });

            // 转回数组
            const nextList = Array.from(fileMap.values());
            // 存储到 store
            this.setEvaluators(nextList);
            if (isEmpty(this.setStarEvaluator) && nextList.length > 0) {
                this.star_evaluator = nextList[0];
            }
            this.updateEmptyState();
        });
    }

    // 上传文件
    * uploadFile(uploadParams: EvaluatorUploadParams) {
        try {
            this.uploadLoading = true;
            const formData = new FormData();
            formData.append('job_id', uploadParams.job_id); // 添加 job_id 参数
            uploadParams.evaluator.forEach(file => {
                formData.append('evaluator', file);
            });
            const res: ApiResponse = yield evaluateUpload(formData);
            if (res && res.code === '0') {
                this.updateEmptyState();
            } else {
                console.error(res);
                handleRequestError({error: res});
            }
        } catch (err) {
            console.error(err);
            handleRequestError({error: err});
        } finally {
            this.uploadLoading = false;
        }
    }
    /** 根据bos 链接获取文件内容 */
    * fetchFileInfo(selectedFile: FileInfo): Generator<any, string | undefined, unknown> {
        try {
            // 通过 BOS URL 获取文件内容
            const response: any = yield fetch(selectedFile.bos_url);
            if (!response.ok) {
                throw new Error('文件内容加载失败');
            }
            const text = yield response.text();
            // 更新 selectedFile 的代码内容
            return text;
        }
        catch (error) {
            console.error('加载文件失败:', error);
            throw error;
        }
    }

    addDataset(dataset: FileInfo) {
        this.dataset.push(dataset);
    }

    addSimulator(simulator: FileInfo) {
        this.simulators = [simulator];
    }

    toJSON() {
        // 处理单个文件对象的函数
        const processFile = (file: any) => {
            // 排除运行时字段（originFile/encoding）和 bos_url
            const {originFile, encoding, bos_url, ...rest} = file;

            // 如果文件没有被用户编辑过（isCodeEdited 为 false/undefined），排除 code
            // 原因：code 可能被 BOS 异步加载（见 CodeEditorPanel:113），不应触发 hasChanged
            // 只有用户主动编辑的 code（isCodeEdited=true）才参与比对
            if (!rest.isCodeEdited) {
                const {code, ...restWithoutCode} = rest;
                return restWithoutCode;
            }

            // 用户编辑过的文件，保留 code 用于检测变化
            return rest;
        };

        return {
            evaluators: this.evaluators.map(processFile),
            star_evaluator: this.star_evaluator ? processFile(this.star_evaluator) : this.star_evaluator
        };
    }

    // 更新空状态
    private updateEmptyState() {
        this.isEmpty = !this.hasFiles;
    }
}
